import { cartGetters } from '~/utils/ct';
import type { LineItem } from '~~/types/commercetools';
import { getItemPrice } from '~~/helpers/cart/getTotalPrices';
import { roundToTwoDecimals } from '~~/helpers/roundToTwoDecimals/roundToTwoDecimals';
import type {
  FormatPrintableCurrencyParams,
  GetPrintableCartItemsParams,
  PrintableCartItem,
  PrintableCartTotals,
} from '~~/types/helpers/printableCart';

export function formatPrintableCurrency({ amount, currency, locale }: FormatPrintableCurrencyParams): string {

  try {

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  } catch {

    return `${amount.toFixed(2)} ${currency}`;

  }

}

function getPrintableCurrencyCode(lineItem: LineItem, fallbackCurrencyCode: string): string {

  return lineItem.totalPrice?.currencyCode || lineItem.price?.value?.currencyCode || fallbackCurrencyCode;

}

function getPrintableLineItemTotal(lineItem: LineItem): number {

  const itemPrice = getItemPrice(lineItem);
  const finalTotal = itemPrice.special ?? itemPrice.regular;

  if (finalTotal) {

    return finalTotal;

  }

  const unitPrice = lineItem.price?.value?.centAmount
    ? Number((lineItem.price.value.centAmount / 100).toFixed(2))
    : 0;

  return unitPrice * cartGetters.getItemQty(lineItem);

}

function getPrintableLineItemNet(totalPrice: number, lineItem: LineItem): number {

  const totalGrossCentAmount = lineItem.taxedPrice?.totalGross?.centAmount;
  const totalNetCentAmount = lineItem.taxedPrice?.totalNet?.centAmount;

  if (totalGrossCentAmount != null && totalNetCentAmount != null) {

    const vatAmount = roundToTwoDecimals((totalGrossCentAmount - totalNetCentAmount) / 100);

    return roundToTwoDecimals(totalPrice - vatAmount);

  }

  const vatRate = lineItem.taxRate?.amount ?? 0;

  return roundToTwoDecimals(totalPrice / (1 + vatRate));

}

export function getPrintableCartItems({
  lineItems,
  fallbackCurrencyCode,
  locale,
  fallbackItemName,
}: GetPrintableCartItemsParams): PrintableCartItem[] {

  return lineItems.map((lineItem) => {

    const itemCurrencyCode = getPrintableCurrencyCode(lineItem, fallbackCurrencyCode);
    const totalPrice = roundToTwoDecimals(getPrintableLineItemTotal(lineItem));
    const netPrice = getPrintableLineItemNet(totalPrice, lineItem);
    const vat = roundToTwoDecimals(totalPrice - netPrice);

    return {
      name: cartGetters.getItemName(lineItem) || fallbackItemName,
      quantity: cartGetters.getItemQty(lineItem),
      sku: cartGetters.getItemSku(lineItem),
      netPrice: formatPrintableCurrency({
        amount: netPrice,
        currency: itemCurrencyCode,
        locale,
      }),
      vat: formatPrintableCurrency({
        amount: vat,
        currency: itemCurrencyCode,
        locale,
      }),
      totalPrice: formatPrintableCurrency({
        amount: totalPrice,
        currency: itemCurrencyCode,
        locale,
      }),
    };

  });

}

export function getPrintableCartTotals({
  lineItems,
  fallbackCurrencyCode,
  locale,
}: Omit<GetPrintableCartItemsParams, 'fallbackItemName'>): PrintableCartTotals {

  const currencyCode = lineItems.length && lineItems[0]
    ? getPrintableCurrencyCode(lineItems[0], fallbackCurrencyCode)
    : fallbackCurrencyCode;

  const totals = lineItems.reduce((accumulator, lineItem) => {

    const totalPrice = roundToTwoDecimals(getPrintableLineItemTotal(lineItem));
    const netPrice = getPrintableLineItemNet(totalPrice, lineItem);
    const vat = roundToTwoDecimals(totalPrice - netPrice);

    return {
      net: accumulator.net + netPrice,
      vat: accumulator.vat + vat,
      gross: accumulator.gross + totalPrice,
    };

  }, { net: 0, vat: 0, gross: 0 });

  return {
    netTotal: formatPrintableCurrency({
      amount: roundToTwoDecimals(totals.net),
      currency: currencyCode,
      locale,
    }),
    vatTotal: formatPrintableCurrency({
      amount: roundToTwoDecimals(totals.vat),
      currency: currencyCode,
      locale,
    }),
    grandTotal: formatPrintableCurrency({
      amount: roundToTwoDecimals(totals.gross),
      currency: currencyCode,
      locale,
    }),
  };

}
